import React, { FC } from 'react';
import { useQuery } from 'react-query';
import { FaEdit, FaSignInAlt } from 'react-icons/fa';
import MainLayout from 'components/main-layout/MainLayout';
import FetchTable from 'components/table/FetchTable';
import API from 'core/Api';
import { formatDate } from 'utils/helpers/formatters';
import { CellProps, Column } from 'react-table';
import TooltipWrapper from 'components/TooltipWrapper';
import Button from 'components/Button';
import Badge from 'components/Badge';
import { useHistory } from 'react-router';
import { Link } from 'react-router-dom';
import { ISelectOptionProps } from 'components/form/types';
import { filters, buildFilters } from 'components/table/utils/filtersConfig';
import { useTranslation } from 'react-i18next';
import copyToClipboard from 'utils/helpers/copyToClipboard';
import { ICompany } from '../types';

const Screen: FC = () => {
  const { t } = useTranslation();
  const history = useHistory();

  const {
    isLoading: isLoadingCompanyTypes,
    error: errorCompanyTypes,
    data: companyTypes,
  } = useQuery<ISelectOptionProps[], Error>(
    ['companyTypes'],
    (): Promise<ISelectOptionProps[]> => API.get<ISelectOptionProps[]>('clients/types').then(res => res.data)
  );

  const createClient = () => history.push('/clients/create');

  const columns: Array<Column<ICompany>> = [
    {
      Header: 'ID',
      accessor: 'id',
    },
    {
      Header: 'Име на компанията',
      accessor: acc => acc.client_data.company_name,
      disableSortBy: true,
      Cell: (cell: CellProps<ICompany>) => {
        return <span className="text-break">{cell.value}</span>;
      },
    },

    {
      Header: 'Тип на компанията',
      accessor: 'type',
      disableSortBy: true,
      Cell: cell => {
        if (companyTypes && companyTypes.length) {
          const compType = companyTypes.find(companyType => companyType.value === cell.row.original.type);
          return <>{compType?.label}</>;
        }

        return <>{cell.row.original.type}</>;
      },
    },
    {
      Header: 'Email',
      accessor: acc => acc.client_data.email,
      disableSortBy: true,
      Cell: ({ value }) => {
        const emailPreview = value.length > 30 ? `${value.substr(0, 30)}...` : value;

        return (
          <TooltipWrapper
            id="tooltip-email"
            overlay={`${t('common.copyToClipboard')}: ${value}`}
          >
            <span onClick={() => copyToClipboard(value)}>{emailPreview}</span>
          </TooltipWrapper>
        );
      },
    },
    {
      Header: 'Дата на регистрация',
      accessor: 'created_at',
      Cell: cell => <>{formatDate(new Date(cell.row.original.client_data.created_at), 'dd.mm.Y hh:ii:ss')}</>,
    },
    {
      Header: 'Статус',
      accessor: 'status',
      maxWidth: 80,
      width: 80,
      minWidth: 80,
      Cell: cell => {
        if (cell.row.original.is_active)
          return (
            <Badge
              label="Активен"
              variant="primary"
            />
          );

        return (
          <Badge
            label="Неактивен"
            variant="danger"
          />
        );
      },
    },
    {
      Header: 'Мултиезичност',
      accessor: 'is_multilingual',
      maxWidth: 80,
      width: 80,
      minWidth: 80,
      Cell: cell => {
        if (cell.row.original.client_features.is_multilingual)
          return (
            <Badge
              label="Активен"
              variant="primary"
            />
          );

        return (
          <Badge
            label="Неактивен"
            variant="danger"
          />
        );
      },
    },
    {
      Header: 'Шаблони',
      accessor: 'templates',
      maxWidth: 80,
      width: 80,
      minWidth: 80,
      Cell: cell => {
        if (cell.row.original.client_features.templates)
          return (
            <Badge
              label="Активен"
              variant="primary"
            />
          );

        return (
          <Badge
            label="Неактивен"
            variant="danger"
          />
        );
      },
    },
    {
      Header: 'Отпуски',
      accessor: 'leaves',
      maxWidth: 80,
      width: 80,
      minWidth: 80,
      Cell: cell => {
        if (cell.row.original.client_features.leaves)
          return (
            <Badge
              label="Активен"
              variant="primary"
            />
          );

        return (
          <Badge
            label="Неактивен"
            variant="danger"
          />
        );
      },
    },
    {
      Header: () => <div className="text-right">Действия</div>,
      accessor: 'actions',
      disableSortBy: true,
      maxWidth: 100,
      width: 100,
      minWidth: 100,
      Cell: cell => {
        return (
          <div className="text-right">
            <div className="d-inline-block mr-2">
              <TooltipWrapper
                id="tooltip-edit"
                overlay="Редактирай"
              >
                <Button
                  btnType="primary"
                  size="sm"
                  outlined
                  ariaLabel="Edit Client Button"
                  onClick={() => history.push(`/clients/edit/${cell.row.original.id}`)}
                >
                  <FaEdit size={14} />
                </Button>
              </TooltipWrapper>
            </div>
            {/**
             * Use Link here because we need to open the client
             * in new tab when operator clicks on button to enter
             */}
            <TooltipWrapper
              id="tooltip-enter"
              overlay="Вход в клиент"
            >
              <Link
                className="btn btn-sm btn-outline-secondary"
                to={`/client/${cell.row.original.id}`}
                target="_blank"
                aria-label="Enter Client Button"
              >
                <FaSignInAlt size={14} />
              </Link>
            </TooltipWrapper>
          </div>
        );
      },
    },
  ];

  const tableFilters = [
    buildFilters({
      name: 'type',
      placeholder: 'Тип',
      options: companyTypes,
    }),
    buildFilters({ ...filters.status, t, translatable: true }),
  ];

  return (
    <MainLayout
      title="Управление на клиенти"
      // isLoading={isLoadingClients && isLoadingCompanyTypes}
      error={errorCompanyTypes}
    >
      <FetchTable<ICompany>
        loading={isLoadingCompanyTypes}
        columns={columns}
        onAdd={createClient}
        addBtnLabel="Добавяне на клиент"
        filters={tableFilters}
        entityName="clients"
        entityEndpoint="clients"
        pagination
      />
    </MainLayout>
  );
};

export default Screen;
